import { useEffect, useRef, useState, useCallback } from 'react'
import { viewer360Api } from '../api/endpoints'

interface Props {
  /** The supplier Vision360 viewer URL (stone.media.videoUrl). */
  viewerUrl: string
  /**
   * Called when the frame sequence can't be resolved (not a Vision360
   * viewer, dead ref, network error). The parent falls back to the
   * legacy iframe / photo. Called at most once.
   */
  onUnavailable?: () => void
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/**
 * DiaEx's own 360° player. Resolves a supplier Vision360 frame
 * sequence through OUR masking proxy (/api/v) and renders a native
 * drag-to-rotate spin — no archaic supplier chrome, no iframe, no
 * supplier-host/ref leak. Auto-rotates until the first interaction,
 * carries momentum on release, and offers fullscreen. Navy/cream.
 */
export function Diaex360({ viewerUrl, onUnavailable }: Props) {
  const [frames, setFrames] = useState<string[]>([])
  const [loaded, setLoaded] = useState(0)
  const [status, setStatus] = useState<'loading' | 'ready' | 'gone'>('loading')
  const [isFs, setIsFs] = useState(false)
  const [hintGone, setHintGone] = useState(false)
  // "Proxy play" (Vikram, 2026-07-26): auto-rotate by default; a tap pauses so
  // you can drag-to-scrub, another tap resumes the spin. Not a one-way stop.
  const [playing, setPlaying] = useState(true)

  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const loadedIdx = useRef<Set<number>>(new Set()) // which frames are decoded
  const idx = useRef(0)              // current frame (float, wrapped)
  const vel = useRef(0)              // frames/tick for momentum + auto
  const drag = useRef<{ x: number; last: number } | null>(null)
  const moved = useRef(false)        // did this pointer-down turn into a drag?
  const raf = useRef<number | null>(null)
  const notified = useRef(false)

  const fail = useCallback(() => {
    if (notified.current) return
    notified.current = true
    setStatus('gone')
    onUnavailable?.()
  }, [onUnavailable])

  // Resolve the frame sequence server-side.
  useEffect(() => {
    let cancelled = false
    setStatus('loading'); setLoaded(0); setFrames([]); setHintGone(false); setPlaying(true)
    idx.current = 0; vel.current = 0
    viewer360Api.frames(viewerUrl)
      .then((r) => {
        if (cancelled) return
        // Need at least a few frames for a spin to read as 360°.
        if (!r.resolved || r.count < 4) { fail(); return }
        setFrames(r.frames)
      })
      .catch(() => { if (!cancelled) fail() })
    return () => { cancelled = true }
  }, [viewerUrl, fail])

  // Preload frames (browser caches them; immutable + long-lived). Cold-cache
  // frames stream through our proxy one upstream-fetch at a time and can take
  // several seconds each — so DON'T block the reveal on all of them, or the
  // buyer stares at a blank pane (the "it won't play" complaint). Reveal as
  // soon as a couple of frames decode; the rest fill in behind the spin (the
  // auto-rotate is slow enough that stragglers arrive before they're needed).
  useEffect(() => {
    if (frames.length === 0) return
    let alive = true
    let done = 0
    let errs = 0
    let revealed = false
    // Enough frames to start a spin that reads as motion — first frame, or a
    // small threshold, whichever comes first.
    const REVEAL_AT = Math.min(3, frames.length)
    const revealOnce = () => { if (!revealed) { revealed = true; setStatus('ready') } }
    loadedIdx.current = new Set()
    const imgs = frames.map((src, i) => {
      const im = new Image()
      im.onload = () => {
        if (!alive) return
        loadedIdx.current.add(i)
        done++; setLoaded(done)
        if (done >= REVEAL_AT) revealOnce()
      }
      im.onerror = () => {
        if (!alive) return
        errs++; done++; setLoaded(done)
        // Every frame dead ⇒ the proxy resolved a bad sequence → fall back.
        if (errs === frames.length) fail()
        else if (done >= REVEAL_AT) revealOnce()
      }
      im.src = src
      return im
    })
    // Watchdog: if not even a few frames decode within 9s, drop to the legacy
    // iframe / photo instead of spinning on a blank pane forever.
    const wd = setTimeout(() => { if (alive && !revealed) fail() }, 9000)
    return () => { alive = false; clearTimeout(wd); imgs.forEach((im) => { im.onload = null; im.onerror = null }) }
  }, [frames, fail])

  const render = useCallback(() => {
    const n = frames.length
    if (n === 0 || !imgRef.current) return
    let i = Math.round(idx.current) % n
    if (i < 0) i += n
    // While frames are still streaming in, snap to the nearest already-decoded
    // frame so an early reveal never flashes a blank <img>.
    const loaded = loadedIdx.current
    if (loaded.size > 0 && loaded.size < n && !loaded.has(i)) {
      for (let d = 1; d <= n; d++) {
        if (loaded.has((i + d) % n)) { i = (i + d) % n; break }
        if (loaded.has((i - d + n) % n)) { i = (i - d + n) % n; break }
      }
    }
    imgRef.current.src = frames[i]
  }, [frames])

  // Animation loop: auto-rotate while `playing` (and not mid-drag); when paused,
  // let any release-momentum decay to a rest. Re-runs when `playing` toggles.
  useEffect(() => {
    if (status !== 'ready') return
    const n = frames.length
    const reduce = prefersReducedMotion()
    // One full turn in ~14s when auto-rotating.
    const autoStep = reduce ? 0 : n / (14 * 60)

    const tick = () => {
      if (!drag.current) {
        if (playing && !reduce) {
          idx.current += autoStep
        } else {
          idx.current += vel.current
          vel.current *= 0.94                 // momentum friction
          if (Math.abs(vel.current) < 0.002) vel.current = 0
        }
        render()
      }
      raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [status, frames, render, playing])

  // Drag to scrub; a tap (no drag) toggles play/pause. Grabbing pauses the
  // auto-spin for the duration of the drag; releasing after a real drag resumes
  // whatever play state you were in (so a scrub-while-playing snaps back to
  // auto-rotate), while a pure tap flips play↔pause.
  const onDown = (e: React.PointerEvent) => {
    if (status !== 'ready') return
    setHintGone(true)
    moved.current = false
    vel.current = 0
    drag.current = { x: e.clientX, last: e.clientX }
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
  }
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current) return
    const n = frames.length
    const dx = e.clientX - drag.current.last
    if (Math.abs(e.clientX - drag.current.x) > 3) moved.current = true
    drag.current.last = e.clientX
    // ~ one full rotation per container width of travel.
    const w = wrapRef.current?.clientWidth || 400
    const step = (dx / w) * n * 1.2
    idx.current += step
    vel.current = step                    // seed momentum with last motion
    render()
  }
  const onUp = () => {
    const wasDrag = moved.current
    drag.current = null
    // Pure tap (no drag) → toggle auto-rotate on/off. A real drag keeps the
    // current play state (momentum carries, then auto resumes if still playing).
    if (!wasDrag) { vel.current = 0; setPlaying((p) => !p) }
  }

  const toggleFs = useCallback(() => {
    const el = wrapRef.current
    if (!el) return
    if (!document.fullscreenElement) el.requestFullscreen?.().catch(() => {})
    else document.exitFullscreen?.().catch(() => {})
  }, [])
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFs)
    return () => document.removeEventListener('fullscreenchange', onFs)
  }, [])

  if (status === 'gone') return null

  const pct = frames.length ? Math.round((loaded / frames.length) * 100) : 0

  return (
    <div
      ref={wrapRef}
      className="absolute inset-0 flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#f6f8fb] to-[#e9eef4] select-none touch-none"
      data-testid="diaex360"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      style={{ cursor: status === 'ready' ? 'grab' : 'default' }}
    >
      <img
        ref={imgRef}
        alt="360° view"
        draggable={false}
        className={
          'pointer-events-none max-h-full max-w-full object-contain transition-opacity duration-500 ' +
          (status === 'ready' ? 'opacity-100' : 'opacity-0')
        }
      />

      {/* Progress ring while frames buffer — subtle, centered. */}
      {status !== 'ready' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-ink/80">
          <svg width="46" height="46" viewBox="0 0 46 46" className="-rotate-90">
            <circle cx="23" cy="23" r="20" fill="none" stroke="rgba(12,31,58,0.12)" strokeWidth="3" />
            <circle
              cx="23" cy="23" r="20" fill="none" stroke="#0c1f3a" strokeWidth="3" strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 20}
              strokeDashoffset={(2 * Math.PI * 20) * (1 - pct / 100)}
              style={{ transition: 'stroke-dashoffset 0.2s ease' }}
            />
          </svg>
          <span className="text-[11px] font-semibold uppercase tracking-widest text-ink/60">
            {pct}%
          </span>
        </div>
      )}

      {/* Drag hint — fades out once the buyer takes over. */}
      {status === 'ready' && !hintGone && (
        <div
          className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-navy/10 px-3 py-1.5 text-[11px] font-medium text-ink/80 backdrop-blur-sm"
          data-testid="diaex360-hint"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 3-6.7" /><polyline points="3 3 3 9 9 9" />
          </svg>
          Tap to pause · drag to spin
        </div>
      )}

      {/* Play / pause — mirrors the auto-spin state; tap the stone toggles it too. */}
      {status === 'ready' && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setHintGone(true); vel.current = 0; setPlaying((p) => !p) }}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={playing ? 'Pause rotation' : 'Play rotation'}
          data-testid="diaex360-play"
          className="absolute bottom-3 left-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-navy/10 text-ink backdrop-blur-md transition-colors hover:bg-navy/20"
        >
          {playing ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
          ) : (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M7 5.5v13a1 1 0 0 0 1.5.87l11-6.5a1 1 0 0 0 0-1.74l-11-6.5A1 1 0 0 0 7 5.5Z" /></svg>
          )}
        </button>
      )}

      {/* Fullscreen — the only chrome; premium, minimal. */}
      {status === 'ready' && (
        <button
          type="button"
          onClick={toggleFs}
          aria-label={isFs ? 'Exit fullscreen' : 'Fullscreen'}
          data-testid="diaex360-fullscreen"
          className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-navy/10 text-ink backdrop-blur-md transition-colors hover:bg-navy/20"
        >
          {isFs ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8V5a2 2 0 0 1 2-2h3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M21 16v3a2 2 0 0 1-2 2h-3" />
            </svg>
          )}
        </button>
      )}
    </div>
  )
}
